import { useState } from "react";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { getMyPermissions } from "../api/users";
import {
  controlSpamProbeQueue,
  enqueueSpamProbeQueue,
  getQuestionableDidCandidates,
  getSpamProbeQueueStatus,
  listSpamProbeQueueItems,
  type QueueControlAction,
  type SpamProbeHandsetSlot,
  type SpamProbeQueueItem,
} from "../api/spamDetection";

function fmtPhone(raw: string | null | undefined): string {
  if (!raw) return "—";
  const d = raw.replace(/\D/g, "");
  const ten = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
  if (ten.length === 10) {
    return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
  }
  return raw;
}

function fmtEta(seconds: number | null | undefined): string {
  const s = Math.max(0, Math.round(seconds ?? 0));
  if (s === 0) return "Idle";
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m === 0) return `${rem}s`;
  if (m < 60) return `${m}m ${rem}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function fmtTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    const dt = new Date(iso);
    return dt.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}

function readApiError(e: unknown): string {
  const ax = e as {
    response?: { data?: { detail?: string; error?: string } };
    message?: string;
  };
  return (
    ax.response?.data?.detail ||
    ax.response?.data?.error ||
    ax.message ||
    "Request failed"
  );
}

function OutcomeBadge({ item }: { item: SpamProbeQueueItem }) {
  if (item.final_outcome === "spam") {
    return (
      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-800">
        SPAM · {item.verdict_label || "CONVICTED"}
      </span>
    );
  }
  if (item.final_outcome === "clean") {
    return (
      <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
        CLEAN
      </span>
    );
  }
  if (item.final_outcome === "inconclusive") {
    return (
      <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800">
        INCONCLUSIVE · {item.verdict_label || "TIMEOUT"}
      </span>
    );
  }
  const stMap: Record<string, string> = {
    queued: "bg-slate-100 text-slate-700",
    retry_queued: "bg-indigo-100 text-indigo-800",
    dialing: "bg-blue-100 text-blue-800 animate-pulse",
    awaiting_verdict: "bg-sky-100 text-sky-800 animate-pulse",
    cancelled: "bg-gray-100 text-gray-500",
  };
  return (
    <span
      className={`px-2 py-0.5 rounded text-xs font-medium ${
        stMap[item.status] || "bg-gray-100 text-gray-600"
      }`}
    >
      {item.status.replace("_", " ")} (try {item.attempts}/{item.max_attempts})
    </span>
  );
}

function ReplacementCell({ item }: { item: SpamProbeQueueItem }) {
  if (item.final_outcome !== "spam" && item.replace_status === "none") {
    return <span className="text-xs text-gray-400">—</span>;
  }
  const repBadge =
    item.replace_status === "replaced"
      ? "bg-emerald-100 text-emerald-800"
      : item.replace_status === "queued" || item.replace_status === "quarantined"
      ? "bg-amber-100 text-amber-800"
      : item.replace_status === "no_stock"
      ? "bg-red-100 text-red-800"
      : "bg-gray-100 text-gray-600";

  const sellBadge =
    item.selloff_status === "sold_off"
      ? "bg-purple-100 text-purple-800"
      : item.selloff_status === "pending"
      ? "bg-amber-50 text-amber-700"
      : item.selloff_status === "failed"
      ? "bg-red-50 text-red-700"
      : "bg-gray-100 text-gray-500";

  return (
    <div className="flex flex-col gap-1 text-xs">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className={`px-1.5 py-0.5 rounded font-medium ${repBadge}`}>
          {item.replace_status === "replaced" && item.replacement_phone
            ? `→ ${fmtPhone(item.replacement_phone)}`
            : item.replace_status}
        </span>
        {item.selloff_status !== "none" && (
          <span
            className={`px-1.5 py-0.5 rounded ${sellBadge}`}
            title={item.sold_off_at ? `Sold off at ${item.sold_off_at}` : undefined}
          >
            {item.selloff_status === "sold_off"
              ? "Bandwidth Sold Off"
              : `selloff: ${item.selloff_status}`}
          </span>
        )}
      </div>
    </div>
  );
}

function HandsetSlotCard({ slot }: { slot: SpamProbeHandsetSlot }) {
  const isRinging = slot.state === "ringing";
  const isDegraded = slot.state === "degraded_cooldown";
  const badgeCls = isRinging
    ? "bg-blue-100 text-blue-800 animate-pulse"
    : slot.state === "cooldown"
    ? "bg-amber-100 text-amber-800"
    : isDegraded
    ? "bg-red-100 text-red-800"
    : "bg-emerald-50 text-emerald-700";

  return (
    <div
      className={`bg-white rounded-lg border p-3 shadow-sm ${
        isRinging ? "border-blue-400 ring-1 ring-blue-100" : "border-gray-200"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono font-semibold text-sm text-gray-900">
          {fmtPhone(slot.phone)}
        </span>
        <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${badgeCls}`}>
          {slot.state || "idle"}
        </span>
      </div>
      <div className="text-xs text-gray-500 mt-0.5 truncate">
        {slot.carrier_name} · {slot.model || slot.device_name}
      </div>
      <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-xs">
        <div className="truncate text-gray-700">
          {slot.current_did ? (
            <span>
              Dialing <span className="font-mono font-medium">{fmtPhone(slot.current_did)}</span>
              {slot.current_tenant ? ` (${slot.current_tenant})` : ""}
            </span>
          ) : (
            <span className="text-gray-400">Ready for next DID</span>
          )}
        </div>
        <div className="text-gray-500 tabular-nums shrink-0 ml-2">
          {slot.verdicts_received}/{slot.calls_completed} verdicts
        </div>
      </div>
    </div>
  );
}

export function DidSpamDetectionPage() {
  const qc = useQueryClient();
  const { data: me } = useQuery({
    queryKey: ["me-permissions"],
    queryFn: getMyPermissions,
  });
  const isAdmin = me ? Boolean(me.is_admin) : true;

  const [activeTab, setActiveTab] = useState<"queue" | "candidates">("queue");
  const [selectedTenant, setSelectedTenant] = useState<number | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [outcomeFilter, setOutcomeFilter] = useState<string>("");
  const [replaceFilter, setReplaceFilter] = useState<string>("");

  // Scan / Enqueue configuration controls
  const [lookbackHours, setLookbackHours] = useState<number>(48);
  const [minRejects, setMinRejects] = useState<number>(3);
  const [pickupDeficitPct, setPickupDeficitPct] = useState<number>(5.0);
  const [autoReplace, setAutoReplace] = useState<boolean>(true);
  const [immediateSelloff, setImmediateSelloff] = useState<boolean>(true);
  const [forceReprobe, setForceReprobe] = useState<boolean>(false);
  const [banner, setBanner] = useState<{ tone: "ok" | "err"; text: string } | null>(
    null
  );

  const statusQ = useQuery({
    queryKey: ["spam-probe-queue", "status", selectedTenant],
    queryFn: () => getSpamProbeQueueStatus({ usr_id: selectedTenant }),
    refetchInterval: 4000,
  });

  const itemsQ = useQuery({
    queryKey: [
      "spam-probe-queue",
      "items",
      selectedTenant,
      statusFilter,
      outcomeFilter,
      replaceFilter,
    ],
    queryFn: () =>
      listSpamProbeQueueItems({
        usr_id: selectedTenant,
        status: statusFilter || undefined,
        outcome: outcomeFilter || undefined,
        replace_status: replaceFilter || undefined,
        limit: 300,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: 4000,
  });

  const candidatesQ = useQuery({
    queryKey: [
      "spam-probe-queue",
      "candidates",
      selectedTenant,
      lookbackHours,
      minRejects,
      pickupDeficitPct,
      forceReprobe,
    ],
    queryFn: () =>
      getQuestionableDidCandidates({
        usr_id: selectedTenant,
        lookback_hours: lookbackHours,
        min_rejects: minRejects,
        pickup_deficit_pct: pickupDeficitPct,
        force_reprobe: forceReprobe,
        limit: 500,
      }),
    enabled: activeTab === "candidates",
    placeholderData: keepPreviousData,
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["spam-probe-queue"] });
  };

  const enqueueMut = useMutation({
    mutationFn: () =>
      enqueueSpamProbeQueue({
        lookback_hours: lookbackHours,
        min_rejects: minRejects,
        pickup_deficit_pct: pickupDeficitPct,
        usr_id: selectedTenant ?? null,
        force_reprobe: forceReprobe,
        auto_start: true,
        auto_replace: autoReplace,
        immediate_selloff: immediateSelloff,
      }),
    onSuccess: (res) => {
      setBanner({
        tone: "ok",
        text: `Enqueued ${res.enqueued} questionable DIDs (${res.skipped_already_queued} already queued)${
          res.runner_started ? " and started the 6-phone worker pool." : "."
        }`,
      });
      setActiveTab("queue");
      invalidateAll();
    },
    onError: (e) => setBanner({ tone: "err", text: readApiError(e) }),
  });

  const controlMut = useMutation({
    mutationFn: (action: QueueControlAction) =>
      controlSpamProbeQueue({ action }),
    onSuccess: (_res, action) => {
      setBanner({
        tone: "ok",
        text: `Executed queue control action: ${action.replace("_", " ")}`,
      });
      invalidateAll();
    },
    onError: (e) => setBanner({ tone: "err", text: readApiError(e) }),
  });

  const st = statusQ.data;
  const counts = st?.counts;
  const items = itemsQ.data?.items ?? [];
  const candidates = candidatesQ.data?.candidates ?? [];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold text-gray-900">
              Post-Hours Questionable DID Spam Detection Queue
            </h1>
            {st?.runner_active ? (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 animate-pulse">
                Worker Pool Active · ETA {fmtEta(st.eta_seconds)}
              </span>
            ) : (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                Worker Pool Idle
              </span>
            )}
            {st?.quiet_window_now && (
              <span className="px-2 py-0.5 rounded text-xs bg-indigo-50 text-indigo-700">
                Post-Dialing Quiet Window (00:05–07:50 ET)
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Automatically detects questionable DIDs across all tenants (&ge;{minRejects} SIP 603 /
            Q.850 Cause 21 declines AND pickup rate &le; peer &minus; {pickupDeficitPct}%), probes
            them concurrently across our 6 physical Android handsets, and immediately replaces &amp;
            sells off convicted Spam Likely DIDs at Bandwidth.
          </p>
        </div>

        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => enqueueMut.mutate()}
              disabled={enqueueMut.isPending}
              className="px-3.5 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 shadow-sm"
            >
              {enqueueMut.isPending
                ? "Scanning & Enqueuing…"
                : "Scan & Enqueue Questionable DIDs"}
            </button>
            {st?.runner_active ? (
              <button
                type="button"
                onClick={() => controlMut.mutate("pause")}
                disabled={controlMut.isPending}
                className="px-3 py-2 rounded-lg text-sm font-medium bg-amber-100 text-amber-900 hover:bg-amber-200"
              >
                Pause Queue
              </button>
            ) : (
              <button
                type="button"
                onClick={() => controlMut.mutate("start")}
                disabled={controlMut.isPending}
                className="px-3 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Start / Resume Workers
              </button>
            )}
            <button
              type="button"
              onClick={() => controlMut.mutate("retry_inconclusive")}
              disabled={controlMut.isPending}
              className="px-3 py-2 rounded-lg text-sm font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Retry Inconclusive
            </button>
            <button
              type="button"
              onClick={() => controlMut.mutate("drain_replacements")}
              disabled={controlMut.isPending}
              className="px-3 py-2 rounded-lg text-sm font-medium bg-purple-600 text-white hover:bg-purple-700"
            >
              Replace &amp; Sell Off Convicted Now
            </button>
          </div>
        )}
      </div>

      {banner && (
        <div
          className={`rounded-lg px-4 py-2.5 text-sm flex items-center justify-between ${
            banner.tone === "ok"
              ? "bg-emerald-50 text-emerald-900 border border-emerald-200"
              : "bg-red-50 text-red-900 border border-red-200"
          }`}
        >
          <span>{banner.text}</span>
          <button
            type="button"
            onClick={() => setBanner(null)}
            className="text-xs underline ml-4"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* KPI Summary Tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          <div className="text-[11px] uppercase tracking-wide text-gray-400">Queued</div>
          <div className="text-2xl font-bold tabular-nums text-gray-900">
            {(counts?.queued ?? 0).toLocaleString()}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          <div className="text-[11px] uppercase tracking-wide text-gray-400">In Flight</div>
          <div className="text-2xl font-bold tabular-nums text-blue-700">
            {(counts?.in_progress ?? 0).toLocaleString()}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          <div className="text-[11px] uppercase tracking-wide text-gray-400">Completed</div>
          <div className="text-2xl font-bold tabular-nums text-gray-800">
            {(counts?.completed ?? 0).toLocaleString()}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          <div className="text-[11px] uppercase tracking-wide text-gray-400">Verified Clean</div>
          <div className="text-2xl font-bold tabular-nums text-emerald-700">
            {(counts?.clean ?? 0).toLocaleString()}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-3">
          <div className="text-[11px] uppercase tracking-wide text-red-500">Spam Convicted</div>
          <div className="text-2xl font-bold tabular-nums text-red-700">
            {(counts?.spam ?? 0).toLocaleString()}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          <div className="text-[11px] uppercase tracking-wide text-gray-400">Replaced 1-for-1</div>
          <div className="text-2xl font-bold tabular-nums text-emerald-800">
            {(counts?.replaced ?? 0).toLocaleString()}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          <div className="text-[11px] uppercase tracking-wide text-gray-400">Bandwidth Sold Off</div>
          <div className="text-2xl font-bold tabular-nums text-purple-700">
            {(counts?.sold_off ?? 0).toLocaleString()}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          <div className="text-[11px] uppercase tracking-wide text-gray-400">Inconclusive</div>
          <div className="text-2xl font-bold tabular-nums text-amber-700">
            {(counts?.inconclusive ?? 0).toLocaleString()}
          </div>
        </div>
      </div>

      {/* 6-Handset Live Worker Pool */}
      {isAdmin && st?.handset_slots && st.handset_slots.length > 0 && (
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
            Physical Probe Handset Worker Pool ({st.handset_slots.length} Phones · ~24 DIDs/min Concurrent)
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            {st.handset_slots.map((slot) => (
              <HandsetSlotCard key={slot.line_id} slot={slot} />
            ))}
          </div>
        </div>
      )}

      {/* Multi-Tenant Summary & Scan Parameters */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-gray-800">
              Multi-Tenant Queue Breakdown
            </h2>
            {selectedTenant !== undefined && (
              <button
                type="button"
                onClick={() => setSelectedTenant(undefined)}
                className="text-xs text-blue-600 hover:underline"
              >
                Show All Tenants
              </button>
            )}
          </div>
          {st?.by_tenant && st.by_tenant.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-400 border-b border-gray-100">
                    <th className="py-1.5 pr-3">Tenant</th>
                    <th className="py-1.5 px-2 text-right">Queued/Active</th>
                    <th className="py-1.5 px-2 text-right">Clean</th>
                    <th className="py-1.5 px-2 text-right">Spam</th>
                    <th className="py-1.5 px-2 text-right">Replaced</th>
                    <th className="py-1.5 px-2 text-right">Sold Off</th>
                    <th className="py-1.5 pl-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {st.by_tenant.map((t) => {
                    const selected = selectedTenant === t.usr_id;
                    return (
                      <tr
                        key={t.usr_id}
                        onClick={() =>
                          setSelectedTenant(selected ? undefined : t.usr_id)
                        }
                        className={`cursor-pointer hover:bg-gray-50 ${
                          selected ? "bg-blue-50/70 font-semibold" : ""
                        }`}
                      >
                        <td className="py-1.5 pr-3 text-gray-900">
                          {t.username}{" "}
                          <span className="text-gray-400 font-normal">
                            (#{t.usr_id})
                          </span>
                        </td>
                        <td className="py-1.5 px-2 text-right tabular-nums text-blue-700">
                          {t.pending_items}
                        </td>
                        <td className="py-1.5 px-2 text-right tabular-nums text-emerald-700">
                          {t.clean_items}
                        </td>
                        <td className="py-1.5 px-2 text-right tabular-nums text-red-700 font-medium">
                          {t.spam_items}
                        </td>
                        <td className="py-1.5 px-2 text-right tabular-nums text-emerald-800">
                          {t.replaced_items}
                        </td>
                        <td className="py-1.5 px-2 text-right tabular-nums text-purple-700">
                          {t.sold_off_items}
                        </td>
                        <td className="py-1.5 pl-2 text-right tabular-nums text-gray-700">
                          {t.total_items}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-xs text-gray-400 py-3">
              No queued items recorded yet. Click &ldquo;Scan &amp; Enqueue Questionable DIDs&rdquo;
              to scan all tenants.
            </p>
          )}
        </div>

        {/* Detection Thresholds Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-3">
          <h2 className="text-sm font-semibold text-gray-800">
            Questionable DID Detection Rules
          </h2>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <label className="block">
              <span className="text-gray-500">Lookback (h)</span>
              <input
                type="number"
                min={1}
                max={336}
                value={lookbackHours}
                onChange={(e) => setLookbackHours(Number(e.target.value) || 48)}
                className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-gray-900"
              />
            </label>
            <label className="block">
              <span className="text-gray-500">Min 603/C21</span>
              <input
                type="number"
                min={1}
                max={100}
                value={minRejects}
                onChange={(e) => setMinRejects(Number(e.target.value) || 3)}
                className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-gray-900"
              />
            </label>
            <label className="block">
              <span className="text-gray-500">Pickup Deficit %</span>
              <input
                type="number"
                step="0.5"
                min={0}
                max={90}
                value={pickupDeficitPct}
                onChange={(e) => setPickupDeficitPct(Number(e.target.value) || 5)}
                className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-gray-900"
              />
            </label>
          </div>
          {isAdmin && (
            <div className="space-y-1.5 text-xs text-gray-700 pt-1">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={autoReplace}
                  onChange={(e) => setAutoReplace(e.target.checked)}
                />
                <span>Auto-replace Spam Likely DIDs 1-for-1 (same state/NPA &amp; tenant)</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={immediateSelloff}
                  onChange={(e) => setImmediateSelloff(e.target.checked)}
                />
                <span>Disconnect &amp; sell off old DID at Bandwidth ASAP (0h hold)</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={forceReprobe}
                  onChange={(e) => setForceReprobe(e.target.checked)}
                />
                <span>Force re-probe DIDs verified clean within last 18h</span>
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Tabs & Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setActiveTab("queue")}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
                activeTab === "queue"
                  ? "bg-gray-900 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Persistent Probe Queue ({items.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("candidates")}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
                activeTab === "candidates"
                  ? "bg-gray-900 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Live Questionable DID Candidates Preview
              {candidatesQ.data ? ` (${candidatesQ.data.scan.total_candidates})` : ""}
            </button>
          </div>

          {activeTab === "queue" && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded border border-gray-300 px-2 py-1 text-gray-700"
              >
                <option value="">All Statuses</option>
                <option value="queued">Queued</option>
                <option value="dialing">Dialing</option>
                <option value="awaiting_verdict">Awaiting Verdict</option>
                <option value="retry_queued">Retry Queued</option>
                <option value="completed">Completed</option>
              </select>
              <select
                value={outcomeFilter}
                onChange={(e) => setOutcomeFilter(e.target.value)}
                className="rounded border border-gray-300 px-2 py-1 text-gray-700"
              >
                <option value="">All Outcomes</option>
                <option value="spam">Spam Convicted</option>
                <option value="clean">Verified Clean</option>
                <option value="inconclusive">Inconclusive</option>
              </select>
              <select
                value={replaceFilter}
                onChange={(e) => setReplaceFilter(e.target.value)}
                className="rounded border border-gray-300 px-2 py-1 text-gray-700"
              >
                <option value="">All Replacements</option>
                <option value="replaced">Replaced 1-for-1</option>
                <option value="queued">Pending Replacement</option>
                <option value="quarantined">Quarantined Only</option>
              </select>
            </div>
          )}
        </div>

        {activeTab === "queue" ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-left text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="py-2.5 px-3">DID Phone</th>
                  <th className="py-2.5 px-3">Tenant</th>
                  <th className="py-2.5 px-3 text-right">603 / C21</th>
                  <th className="py-2.5 px-3 text-right">Pickup vs Peer</th>
                  <th className="py-2.5 px-3">Probe Handset</th>
                  <th className="py-2.5 px-3">Verdict / Status</th>
                  <th className="py-2.5 px-3">Replacement &amp; Sell-Off</th>
                  <th className="py-2.5 px-3">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-gray-400">
                      No queue items match the current filters.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.queue_id} className="hover:bg-gray-50">
                      <td className="py-2.5 px-3 font-mono font-semibold text-gray-900">
                        {fmtPhone(item.phone)}
                        <div className="text-[11px] font-normal text-gray-400">
                          DID #{item.did_id}
                          {item.did_group_id ? ` · Grp #${item.did_group_id}` : ""}
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-gray-800">
                        {item.username || `tenant_${item.usr_id}`}
                        <span className="text-gray-400 ml-1">(#{item.usr_id})</span>
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums">
                        <span className="font-semibold text-red-700">
                          {item.reject_603_21_count}
                        </span>
                        <span className="text-gray-400"> / {item.total_calls} calls</span>
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums">
                        <span className="font-medium text-gray-900">
                          {item.pickup_pct.toFixed(1)}%
                        </span>{" "}
                        <span className="text-gray-400">
                          vs {item.peer_pickup_pct.toFixed(1)}%
                        </span>
                        <div className="text-[11px] text-amber-700">
                          {item.pickup_delta_pct > 0 ? "+" : ""}
                          {item.pickup_delta_pct.toFixed(1)}%
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        {item.assigned_phone ? (
                          <div>
                            <span className="font-mono text-gray-800">
                              {fmtPhone(item.assigned_phone)}
                            </span>
                            <div className="text-[11px] text-gray-400">
                              {item.assigned_carrier || "Probe"}
                              {item.ami_hangup_cause
                                ? ` · Q.850=${item.ami_hangup_cause}`
                                : ""}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">Waiting</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3">
                        <OutcomeBadge item={item} />
                      </td>
                      <td className="py-2.5 px-3">
                        <ReplacementCell item={item} />
                      </td>
                      <td className="py-2.5 px-3 text-gray-500 whitespace-nowrap">
                        {fmtTime(item.completed_at || item.updated_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-left text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="py-2.5 px-3">DID Phone</th>
                  <th className="py-2.5 px-3">Tenant</th>
                  <th className="py-2.5 px-3 text-right">603 / Cause 21</th>
                  <th className="py-2.5 px-3 text-right">Answered / Total</th>
                  <th className="py-2.5 px-3 text-right">Pickup vs Peer</th>
                  <th className="py-2.5 px-3 text-right">Priority</th>
                  <th className="py-2.5 px-3">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {candidates.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-gray-400">
                      {candidatesQ.isFetching
                        ? "Scanning CDRs across all tenants…"
                        : "No unqueued DIDs currently match the questionable threshold."}
                    </td>
                  </tr>
                ) : (
                  candidates.map((c) => (
                    <tr key={c.did_id} className="hover:bg-gray-50">
                      <td className="py-2.5 px-3 font-mono font-semibold text-gray-900">
                        {fmtPhone(c.phone)}
                        <span className="text-gray-400 font-normal ml-1.5">
                          (#{c.did_id})
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-gray-800">
                        {c.username}{" "}
                        <span className="text-gray-400">(#{c.usr_id})</span>
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums font-semibold text-red-700">
                        {c.reject_603_21_count}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-gray-700">
                        {c.answered_calls} / {c.total_calls}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums">
                        <span className="font-medium text-gray-900">
                          {c.pickup_pct.toFixed(1)}%
                        </span>{" "}
                        <span className="text-gray-400">
                          vs {c.peer_pickup_pct.toFixed(1)}% (
                          {c.pickup_delta_pct.toFixed(1)}%)
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-gray-600">
                        {c.priority}
                      </td>
                      <td className="py-2.5 px-3 text-gray-600 font-mono text-[11px]">
                        {c.reason}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
